// Normalises the product's customAttributes into a consistent shape for the
// drawer renderer. Accepts legacy flat arrays (today's shape) and the new
// sections + fields shape from the spec. Always returns:
//   { sections: [{ id, title, sequenceNo, icon, description }], fields: [...] }
// If no sections are defined, everything lives in one default section.

const DEFAULT_SECTION = { id: "default", title: "Details", sequenceNo: 1 };

const TYPE_ALIASES = {
  // Legacy codes already in prod — keep rendering as today.
  STR: "STR", TXT: "TXT", NUM: "NUM", DAT: "DAT",
  LST: "LST", YEN: "YEN", IMG: "IMG",
  // New codes from spec v2.
  QTY: "QTY", CHP: "CHP", MCH: "MCH", TGL: "TGL",
  RDC: "RDC", GAL: "GAL", UPL: "UPL", AUD: "AUD",
  ADG: "ADG", DTT: "DTT",
  // Multiple image upload.
  MIM: "MIM",
};

function normaliseField(f, fallbackSectionId) {
  if (!f || !f.name) return null;
  const type = TYPE_ALIASES[f.type] || "STR";
  const out = {
    name: f.name,
    type,
    sectionId: f.sectionId || fallbackSectionId,
    sequenceNo: f.sequenceNo ?? 999,
    mandatory: f.mandatory === "Y" || f.required === true ? "Y" : "N",
    hint: f.hint || "",
    placeholder: f.placeholder || "",
    defaultValue: f.defaultValue ?? f.default ?? null,
  };

  // type-specific passthroughs
  if (["CHP", "MCH", "TGL", "RDC", "LST"].includes(type)) {
    // v2 shape uses options[]; legacy may pass `values` as comma-separated or array
    if (Array.isArray(f.options)) {
      out.options = f.options.map((o) => ({
        value: o.value ?? o.label,
        label: o.label ?? o.value ?? String(o),
        priceMultiplier: o.priceMultiplier ?? o.multiplier ?? null,
        priceDelta: o.priceDelta ?? o.delta ?? 0,
        default: o.default === true,
        icon: o.icon || null,
        image: o.image || null,
      }));
    } else if (typeof f.values === "string") {
      out.options = f.values
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => ({ value: s, label: s, priceDelta: 0 }));
    } else if (Array.isArray(f.values)) {
      out.options = f.values.map((v) => ({ value: v, label: v, priceDelta: 0 }));
    } else {
      out.options = [];
    }
    out.minSelect = f.minSelect ?? 0;
    out.maxSelect = f.maxSelect ?? (type === "MCH" ? 99 : 1);
    out.columns = f.columns ?? (type === "RDC" ? 4 : null);
  }

  if (type === "NUM" || type === "QTY") {
    out.min = f.min ?? (type === "QTY" ? 1 : null);
    out.max = f.max ?? (type === "QTY" ? 99 : null);
    out.step = f.step ?? 1;
    out.decimals = f.decimals ?? 0;
    out.unit = f.unit || "";
    // "addon" = entered value is added to the per-unit price (e.g. Additional Charges)
    if (f.priceImpact) out.priceImpact = f.priceImpact;
  }

  if (type === "STR" || type === "TXT") {
    out.maxLength = f.maxLength ?? (type === "TXT" ? 1000 : 200);
    out.pattern = f.pattern || null;
    out.rows = f.rows ?? 3;
  }

  if (type === "DAT" || type === "DTT") {
    out.minDate = f.min || null; // "today" | ISO string
    out.maxDate = f.max || null;
    out.dateStep = f.step ?? 15;
  }

  if (type === "GAL") {
    out.source = f.source || null;
    out.multi = f.multi === "Y" || f.multi === true;
    out.galleryItems = Array.isArray(f.items) ? f.items : null; // inline items fallback
  }

  if (type === "UPL" || type === "MIM") {
    out.maxFiles = f.maxFiles ?? (type === "MIM" ? 10 : 5);
    out.acceptFormats = f.acceptFormats || "jpg,jpeg,png,heic";
    out.maxFileSizeMB = f.maxFileSizeMB ?? 5;
  }

  if (type === "AUD") {
    out.maxDurationSec = f.maxDurationSec ?? 120;
  }

  if (type === "ADG") {
    out.items = Array.isArray(f.items)
      ? f.items.map((it) => ({
          id: it.id || it.sku || it.name,
          name: it.name,
          description: it.description || "",
          price: Number(it.price) || 0,
          sku: it.sku || null,
          image: it.image || null,
        }))
      : [];
    out.minSelect = f.minSelect ?? 0;
    out.maxSelect = f.maxSelect ?? 99;
    out.columns = f.columns ?? 3;
  }

  return out;
}

export function normaliseConfig(raw) {
  // raw can be: array of fields (legacy), or { sections, fields }
  if (!raw) return { sections: [DEFAULT_SECTION], fields: [] };

  if (Array.isArray(raw)) {
    const fields = raw.map((f) => normaliseField(f, DEFAULT_SECTION.id)).filter(Boolean);
    return { sections: [DEFAULT_SECTION], fields };
  }

  const sectionsIn = Array.isArray(raw.sections) && raw.sections.length ? raw.sections : [DEFAULT_SECTION];
  const sections = sectionsIn
    .map((s, i) => ({
      id: s.id || `sec-${i}`,
      title: s.title || "Section",
      sequenceNo: s.sequenceNo ?? i,
      icon: s.icon || "",
      description: s.description || "",
    }))
    .sort((a, b) => a.sequenceNo - b.sequenceNo);

  const fieldsIn = Array.isArray(raw.fields) ? raw.fields : Array.isArray(raw.fileds) ? raw.fileds : [];
  const fields = fieldsIn
    .map((f) => normaliseField(f, sections[0]?.id || DEFAULT_SECTION.id))
    .filter(Boolean)
    .sort((a, b) => a.sequenceNo - b.sequenceNo);

  return { sections, fields };
}

export function buildInitialValues(cfg) {
  const v = {};
  (cfg.fields || []).forEach((f) => {
    if (f.defaultValue !== null && f.defaultValue !== undefined) {
      v[f.name] = f.defaultValue;
      return;
    }
    switch (f.type) {
      case "MCH":
      case "UPL":
      case "MIM":
      case "IMG":
      case "ADG":
        v[f.name] = [];
        break;
      case "GAL":
        v[f.name] = f.multi ? [] : null;
        break;
      case "QTY":
        v[f.name] = f.min || 1;
        break;
      case "NUM":
        v[f.name] = "";
        break;
      case "CHP":
      case "TGL":
      case "RDC":
      case "LST": {
        const def = (f.options || []).find((o) => o.default);
        v[f.name] = def ? def.value : null;
        break;
      }
      case "AUD":
        v[f.name] = null;
        break;
      default:
        v[f.name] = "";
    }
  });
  return v;
}

export function fieldsBySection(cfg, sectionId) {
  return (cfg.fields || []).filter((f) => f.sectionId === sectionId);
}
